import { useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControlLabel,
  Box,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useGetRole, useCreateRole, useUpdateRole } from "../../api/hooks/usePermission";
import { showSuccess, showError } from "../../helpers/toast";
import type { Permission } from "../../api/types/permission.types";
import { MODULE_LIST } from "../../constants/modules";

const AddRole = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const roleId = location?.state?.roleId;
  const isEditMode = !!roleId;

  const [roleName, setRoleName] = useState("");
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch role data for edit mode
  const { data: roleData, isLoading: isLoadingRole, error: roleError } = useGetRole(
    roleId || '',
    { enabled: isEditMode }
  );

  // Initialize permissions from modules
  useEffect(() => {
    if (!isEditMode) {
      const initialPermissions: Permission[] = MODULE_LIST.map((module) => ({
        sideBarId: module.id,
        label: module.label,
        isView: false,
        isAdd: false,
        isEdit: false,
        isDelete: false,
      }));
      setPermissions(initialPermissions);
    }
  }, [isEditMode]);

  // Update form fields when role data is loaded
  useEffect(() => {
    if (roleData?.data && isEditMode) {
      const role = roleData.data;
      setRoleName(role.name || "");
      
      // Merge with module list to ensure all modules are present
      const existingPermissions = role.permission || [];
      const mergedPermissions: Permission[] = MODULE_LIST.map((module) => {
        const existing = existingPermissions.find(p => p.sideBarId === module.id);
        return existing || {
          sideBarId: module.id,
          label: module.label,
          isView: false,
          isAdd: false,
          isEdit: false,
          isDelete: false,
        };
      });
      setPermissions(mergedPermissions);
    }
  }, [roleData, isEditMode]);

  // Handle error
  useEffect(() => {
    if (roleError) {
      showError((roleError as any)?.response?.data?.message || "Failed to load role details");
    }
  }, [roleError]);

  // Create role mutation
  const { mutate: createRole, isPending: isCreating } = useCreateRole({
    onSuccess: (response) => {
      showSuccess(response.message || "Role created successfully");
      navigate("/manage-subadmin/roles");
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to create role");
    },
  });

  // Update role mutation
  const { mutate: updateRole, isPending: isUpdating } = useUpdateRole({
    onSuccess: (response) => {
      showSuccess(response.message || "Role updated successfully");
      navigate("/manage-subadmin/roles");
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update role");
    },
  });

  const handlePermissionChange = (
    sideBarId: number,
    field: 'isView' | 'isAddEdit' | 'isDelete',
    checked: boolean
  ) => {
    setPermissions(prev =>
      prev.map(perm => {
        if (perm.sideBarId !== sideBarId) return perm;

        const updated = { ...perm };

        // Handle Add/Edit as a combined field
        if (field === 'isAddEdit') {
          updated.isAdd = checked;
          updated.isEdit = checked;
          
          // If add/edit is checked, view and delete should be auto-selected
          if (checked) {
            updated.isView = true;
            updated.isDelete = true;
          }
        } else if (field === 'isView') {
          updated.isView = checked;
        } else if (field === 'isDelete') {
          updated.isDelete = checked;
          // If delete is checked, view should be auto-selected
          if (checked) {
            updated.isView = true;
          }
          // If delete is unchecked, check if add/edit is still checked
          if (!checked && (updated.isAdd || updated.isEdit)) {
            updated.isView = true;
            updated.isDelete = true;
          }
        }

        return updated;
      })
    );
    
    // Clear permissions error when any permission is selected
    if (checked && errors.permissions) {
      setErrors({ ...errors, permissions: "" });
    }
  };

  const getPermission = (sideBarId: number): Permission | undefined => {
    return permissions.find(p => p.sideBarId === sideBarId);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!roleName.trim()) {
      newErrors.roleName = "Role name is required";
    }

    // Check if at least one permission is selected
    const hasAnyPermission = permissions.some(
      perm => perm.isView || perm.isAdd || perm.isEdit || perm.isDelete
    );

    if (!hasAnyPermission) {
      newErrors.permissions = "At least one permission must be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = {
      name: roleName.trim(),
      permission: permissions,
    };

    if (isEditMode) {
      updateRole({ id: roleId, data: payload });
    } else {
      createRole(payload);
    }
  };

  if (isEditMode && isLoadingRole) {
    return (
      <>
        <div className="main_title">
          <h1>Edit Role</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button>
            {" "} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
            {" "} - <button onClick={() => navigate("/manage-subadmin/roles")}>Manage Roles</button>
            {" "} - Edit Role
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  const isLoading = isCreating || isUpdating;

  return (
    <>
      <div className="main_title">
        <h1>{isEditMode ? "Edit" : "Add"} Role</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {" "} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
          {" "} - <button onClick={() => navigate("/manage-subadmin/roles")}>Manage Roles</button>
          {" "} - {isEditMode ? "Edit" : "Add"} Role
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_50">
                <label htmlFor="role">Role Name <span className="req">*</span></label>
                <TextField
                  id="role"
                  placeholder="Enter Role Name"
                  hiddenLabel
                  fullWidth
                  value={roleName}
                  onChange={(e) => {
                    setRoleName(e.target.value);
                    if (errors.roleName) {
                      setErrors({ ...errors, roleName: "" });
                    }
                  }}
                  error={!!errors.roleName}
                  helperText={errors.roleName}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <TableContainer className="table_container mt_20">
              <Table>
                <TableHead>
                  <TableRow hover>
                    <TableCell>Module Name</TableCell>
                    <TableCell align="center">View</TableCell>
                    <TableCell align="center">Delete</TableCell>
                    <TableCell align="center">Add/Edit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {MODULE_LIST.map((module) => {
                    const perm = getPermission(module.id);
                    if (!perm) return null;

                    const isAddEditChecked = perm.isAdd && perm.isEdit;

                    return (
                      <TableRow hover key={perm.sideBarId}>
                        <TableCell>
                          <strong>{module.label}</strong>
                        </TableCell>
                        <TableCell align="center">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={perm.isView}
                                onChange={(e) => handlePermissionChange(perm.sideBarId, 'isView', e.target.checked)}
                                disabled={isLoading}
                              />
                            }
                            name="check"
                            label=""
                          />
                        </TableCell>
                        <TableCell align="center">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={perm.isDelete}
                                onChange={(e) => handlePermissionChange(perm.sideBarId, 'isDelete', e.target.checked)}
                                disabled={isLoading || isAddEditChecked}
                              />
                            }
                            name="check"
                            label=""
                          />
                        </TableCell>
                        <TableCell align="center">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isAddEditChecked}
                                onChange={(e) => handlePermissionChange(perm.sideBarId, 'isAddEdit', e.target.checked)}
                                disabled={isLoading}
                              />
                            }
                            name="check"
                            label=""
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {errors.permissions && (
              <div style={{ marginTop: '16px', marginBottom: '8px' }}>
                <p className="err_msg">{errors.permissions}</p>
              </div>
            )}

            <div className="form_btn">
              <Button
                className="btn btn_primary"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (isEditMode ? "Updating..." : "Creating...") : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddRole;
